import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Divider,
  IconButton,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import BaseInstance from '../../../../Services/AxiosInstance/BaseInstance';
import { MODELS } from '../../../../Services/Endpoint';

interface DataParserConfigPanelProps {
  nodeId: string;
  nodeData: {
    label: string;
    provider?: string;
    model?: string;
    apiKey?: string;
    attributes?: { name: string; description: string }[];
  };
  onSave: (nodeId: string, data: any) => void;
  onClose: () => void;
}

interface ProvidersResponse {
  [providerName: string]: string[];
}

const DataParserConfigPanel: React.FC<DataParserConfigPanelProps> = ({
  nodeId,
  nodeData,
  onSave,
  onClose,
}) => {
  const [name, setName] = useState(nodeData.label || '');
  const [provider, setProvider] = useState(nodeData.provider || '');
  const [model, setModel] = useState(nodeData.model || '');
  const [apiKey, setApiKey] = useState(nodeData.apiKey || '');
  const [attributes, setAttributes] = useState<{ name: string; description: string }[]>(
    Array.isArray(nodeData.attributes) ? nodeData.attributes : []
  );
  const [providers, setProviders] = useState<ProvidersResponse>({});
  const [models, setModels] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoading(true);
        const response = await BaseInstance.get(MODELS);
        setProviders(response.data);
      } catch (error) {
        console.error('Failed to fetch providers:', error);
        setProviders({});
      } finally {
        setLoading(false);
      }
    };
    fetchProviders();
  }, []);

  useEffect(() => {
    setName(nodeData.label || '');
    setProvider(nodeData.provider || '');
    setModel(nodeData.model || '');
    setApiKey(nodeData.apiKey || '');
    setAttributes(Array.isArray(nodeData.attributes) ? nodeData.attributes : []);
  }, [nodeId, nodeData]);

  useEffect(() => {
    if (!provider) {
      setModels([]);
      return;
    }
    const providerModels = providers[provider] || [];
    setModels(providerModels);
  }, [provider, providers]);

  const addAttribute = () => {
    setAttributes((prev) => [...prev, { name: '', description: '' }]);
  };

  const updateAttribute = (index: number, key: 'name' | 'description', value: string) => {
    setAttributes((prev) => prev.map((attr, i) => (i === index ? { ...attr, [key]: value } : attr)));
  };

  const removeAttribute = (index: number) => {
    setAttributes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const updatedData = {
      label: name,
      provider,
      model,
      apiKey,
      attributes: attributes.filter((a) => a.name.trim() || a.description.trim()),
    };
    onSave(nodeId, updatedData);
  };

  return (
    <Paper
      sx={{
        width: 400,
        height: '100vh',
        p: 3,
        borderRadius: 0,
        borderLeft: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        right: 0,
        zIndex: 1200,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Configure Data Parser
        </Typography>
        <Button onClick={onClose} sx={{ minWidth: 'auto', p: 1 }}>
          <CloseIcon />
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, overflow: 'auto' }}>
        <TextField
          label="Node Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
        />

        <FormControl fullWidth size="small">
          <InputLabel>Provider</InputLabel>
          <Select
            value={provider}
            onChange={(e) => {
              setProvider(e.target.value as string);
              setModel('');
            }}
            label="Provider"
            disabled={loading}
          >
            {loading ? (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Loading providers...
              </MenuItem>
            ) : (
              Object.keys(providers).map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel>Model</InputLabel>
          <Select
            value={model}
            onChange={(e) => setModel(e.target.value as string)}
            label="Model"
            disabled={loading || !provider}
          >
            {models.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="API Key"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
          type="password"
          placeholder="Enter your API key for the selected provider"
        />

        <Divider />

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Attributes to extract
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addAttribute} variant="outlined">
              Add
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {attributes.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No attributes added yet.
              </Typography>
            )}
            {attributes.map((attr, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Attribute Name"
                  value={attr.name}
                  onChange={(e) => updateAttribute(index, 'name', e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
                <TextField
                  label="Description"
                  value={attr.description}
                  onChange={(e) => updateAttribute(index, 'description', e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
                <IconButton aria-label="remove" onClick={() => removeAttribute(index)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          fullWidth
          disabled={!name || !provider || !model}
        >
          Save Configuration
        </Button>
      </Box>
    </Paper>
  );
};

export default DataParserConfigPanel;


